import React, { useState, useEffect, useRef, useCallback } from "react"
import API from "../../utils/API"

const POLL_INTERVAL_MS = 2000

const GENDER_COLORS = {
  M: "#007bff",
  F: "#e83e8c",
}
const GENDER_DEFAULT_COLOR = "#6c757d"

const GENDER_LABELS = {
  M: "Homme",
  F: "Femme",
}

function formatTime(ts) {
  if (!ts) return "-"
  const d = new Date(ts)
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
}

function formatTimeMs(ts) {
  if (!ts) return "-"
  const d = new Date(ts)
  const ms = String(d.getMilliseconds()).padStart(3, "0")
  return `${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}.${ms}`
}

function durationSince(firstSeen) {
  if (!firstSeen) return "?"
  return `${Math.round((Date.now() - firstSeen) / 1000)}s`
}

function GenderBadge({ gender }) {
  const color = GENDER_COLORS[gender] || GENDER_DEFAULT_COLOR
  const label = GENDER_LABELS[gender] || "Inconnu"
  return (
    <span className="badge mr-1" style={{ backgroundColor: color, color: "#fff" }}>
      {label}
    </span>
  )
}

function ActiveFacesPanel({ activeFaces }) {
  if (!activeFaces || activeFaces.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-user-slash fa-2x mb-2 d-block"></i>
        <small>Aucun visage actif</small>
      </div>
    )
  }

  return (
    <div>
      {activeFaces.map((face) => {
        const borderColor = GENDER_COLORS[face.gender] || GENDER_DEFAULT_COLOR
        return (
          <div
            key={face.trackId}
            className="card mb-2"
            style={{ borderLeft: `4px solid ${borderColor}` }}
          >
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>#{face.trackId}</strong>
                <small className="text-muted">
                  <i className="fas fa-clock mr-1"></i>
                  {durationSince(face.firstSeen)}
                </small>
              </div>
              <div>
                <GenderBadge gender={face.gender} />
                <span className="badge badge-light">{face.ageRange || "-"}</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function DetectionsPanel({ detections }) {
  const containerRef = useRef(null)
  const [userScrolled, setUserScrolled] = useState(false)

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return
    setUserScrolled(containerRef.current.scrollTop > 10)
  }, [])

  useEffect(() => {
    if (!userScrolled && containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [detections, userScrolled])

  if (!detections || detections.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-eye-slash fa-2x mb-2 d-block"></i>
        <small>Aucune détection</small>
      </div>
    )
  }

  const sorted = [...detections].reverse()

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{ maxHeight: "60vh", overflowY: "auto" }}
    >
      {sorted.map((det, idx) => {
        const displayFaces = det.rawFaces || det.faces || []
        const acceptedIds = new Set((det.faces || []).map((f) => f.trackId))
        const acceptedCount = acceptedIds.size
        const totalCount = displayFaces.length
        const isEmpty = totalCount === 0
        return (
          <div
            key={idx}
            className={`border-bottom py-1 px-2 ${isEmpty ? "text-muted" : ""}`}
            style={isEmpty ? { opacity: 0.5 } : undefined}
          >
            <div className="d-flex align-items-center mb-1">
              <small className="text-monospace mr-2">{formatTimeMs(det.timestamp)}</small>
              <span className={`badge ${totalCount > 0 ? "badge-info" : "badge-secondary"}`} style={{ minWidth: "24px" }}>
                {totalCount}
              </span>
              <small className="ml-1 text-muted">
                détecté{totalCount !== 1 ? "s" : ""}
              </small>
              {totalCount > 0 && acceptedCount !== totalCount && (
                <span className="badge badge-success ml-1" title="Visages ayant passé le seuil de confiance">
                  {acceptedCount} accepté{acceptedCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {totalCount > 0 && (
              <div className="ml-3" style={{ fontSize: "0.8rem" }}>
                {displayFaces.map((face, fIdx) => {
                  const isFiltered = !acceptedIds.has(face.trackId)
                  return (
                    <div
                      key={fIdx}
                      className="d-flex align-items-center mb-1"
                      style={isFiltered ? { opacity: 0.5 } : undefined}
                    >
                      <span className="text-monospace mr-2">#{face.trackId}</span>
                      <GenderBadge gender={face.gender} />
                      <span className="badge badge-light mr-1">{face.ageRange || "-"}</span>
                      <ConfidenceBar confidence={face.confidence} />
                      {isFiltered && (
                        <span className="badge badge-danger ml-1" style={{ fontSize: "0.65rem" }} title="Filtré (confiance insuffisante)">
                          <i className="fas fa-filter"></i>
                        </span>
                      )}
                      {face.trackEnd && (
                        <span className="badge badge-warning ml-1" title="Fin de suivi">
                          <i className="fas fa-sign-out-alt"></i>
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ConfidenceBar({ confidence }) {
  if (confidence == null) return null
  const pct = Math.round(confidence * 100)
  const color = pct >= 80 ? "#28a745" : pct >= 50 ? "#ffc107" : "#dc3545"
  return (
    <div
      className="d-inline-flex align-items-center ml-1"
      title={`Confiance: ${pct}%`}
      style={{ width: "50px" }}
    >
      <div
        style={{
          width: "100%",
          height: "6px",
          backgroundColor: "#e9ecef",
          borderRadius: "3px",
          overflow: "hidden",
        }}
      >
        <div style={{ width: `${pct}%`, height: "100%", backgroundColor: color, borderRadius: "3px" }} />
      </div>
      <small className="ml-1 text-muted" style={{ fontSize: "0.7rem", minWidth: "28px" }}>{pct}%</small>
    </div>
  )
}

function DecisionsPanel({ decisions }) {
  const [expandedIdx, setExpandedIdx] = useState(null)

  if (!decisions || decisions.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-brain fa-2x mb-2 d-block"></i>
        <small>Aucune décision enregistrée</small>
      </div>
    )
  }

  const sorted = [...decisions].reverse()

  return (
    <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
      {sorted.map((dec, idx) => {
        const isTargeted = dec.result?.decisionType === "targeted"
        const adName = dec.result?.ad?.ad?.name || dec.result?.ad?.name || "Publicité"
        const criteria = dec.result?.matchedCriteria
        const isExpanded = expandedIdx === idx

        return (
          <div key={idx} className="card mb-2">
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-monospace">{formatTime(dec.timestamp)}</small>
                <span className={`badge ${isTargeted ? "badge-success" : "badge-secondary"}`}>
                  {isTargeted ? "CIBLE" : "DEFAUT"}
                </span>
              </div>

              <div className="mb-1">
                <strong style={{ fontSize: "0.85rem" }}>
                  <i className="fas fa-ad mr-1"></i>{adName}
                </strong>
              </div>

              {criteria && (
                <div style={{ fontSize: "0.8rem" }} className="mb-1">
                  {criteria.gender && (
                    <span className="mr-2">
                      <i className="fas fa-venus-mars mr-1 text-muted"></i>
                      {GENDER_LABELS[criteria.gender] || criteria.gender}
                    </span>
                  )}
                  {criteria.ageRange && (
                    <span className="mr-2">
                      <i className="fas fa-birthday-cake mr-1 text-muted"></i>
                      {Array.isArray(criteria.ageRange) ? criteria.ageRange.join(", ") : criteria.ageRange}
                    </span>
                  )}
                  {criteria.audienceSize != null && (
                    <span>
                      <i className="fas fa-users mr-1 text-muted"></i>
                      {criteria.audienceSize}
                    </span>
                  )}
                </div>
              )}

              <div className="d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                <span className="text-muted mr-2">
                  <i className="fas fa-layer-group mr-1"></i>{dec.teamId || "-"}
                </span>
                <span className={dec.cameraConnected ? "text-success" : "text-danger"}>
                  <i className={`fas fa-${dec.cameraConnected ? "video" : "video-slash"} mr-1`}></i>
                  {dec.cameraConnected ? "Caméra OK" : "Caméra OFF"}
                </span>
              </div>

              {dec.activeFacesAtDecision?.length > 0 && (
                <>
                  <button
                    className="btn btn-link btn-sm p-0 mt-1"
                    style={{ fontSize: "0.75rem" }}
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                  >
                    <i className={`fas fa-${isExpanded ? "chevron-up" : "chevron-down"} mr-1`}></i>
                    Visages au moment ({dec.activeFacesAtDecision.length})
                  </button>
                  {isExpanded && (
                    <div className="mt-1 ml-2" style={{ fontSize: "0.75rem" }}>
                      {dec.activeFacesAtDecision.map((f, fIdx) => (
                        <div key={fIdx}>
                          #{f.trackId} — {GENDER_LABELS[f.gender] || "?"} — {f.ageRange || "-"}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function AiDebugModal({ totemId, onClose }) {
  const [snapshot, setSnapshot] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pollError, setPollError] = useState(null)
  const [paused, setPaused] = useState(false)
  const [lastPollTime, setLastPollTime] = useState(null)
  const intervalRef = useRef(null)

  const fetchSnapshot = useCallback(() => {
    API.POST(
      `/admin/totem/${totemId}/ai-targeting-module/debug-snapshot`,
      {},
      (response) => {
        setSnapshot(response.data)
        setLastPollTime(new Date())
        setPollError(null)
        setLoading(false)
      },
      (errorMessage) => {
        setPollError(errorMessage)
        setLoading(false)
      },
    )
  }, [totemId])

  useEffect(() => {
    fetchSnapshot()
  }, [fetchSnapshot])

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      return
    }
    intervalRef.current = setInterval(fetchSnapshot, POLL_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [paused, fetchSnapshot])

  const handleClear = () => {
    setSnapshot(null)
    setLastPollTime(null)
    setPollError(null)
  }

  const detectionCount = snapshot?.detections?.length || 0
  const activeFaceCount = snapshot?.activeFaces?.length || 0
  const decisionCount = snapshot?.decisions?.length || 0

  return (
    <>
      <div className="modal fade show" style={{ display: "block", zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header py-2">
              <h5 className="modal-title">
                <i className="fas fa-bug mr-2"></i>
                Debug IA en temps réel
              </h5>
              <div className="d-flex align-items-center">
                <button
                  className={`btn btn-sm mr-2 ${paused ? "btn-success" : "btn-warning"}`}
                  onClick={() => setPaused((p) => !p)}
                  title={paused ? "Reprendre le polling" : "Mettre en pause"}
                >
                  <i className={`fas fa-${paused ? "play" : "pause"} mr-1`}></i>
                  {paused ? "Reprendre" : "Pause"}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary mr-2"
                  onClick={handleClear}
                  title="Effacer l'affichage"
                >
                  <i className="fas fa-eraser mr-1"></i>Effacer
                </button>
                <button type="button" className="close ml-2" onClick={onClose} style={{ fontSize: "1.2rem" }}>
                  <span>&times;</span>
                </button>
              </div>
            </div>

            {/* Status bar */}
            <div className="px-3 py-2 border-bottom d-flex align-items-center flex-wrap" style={{ fontSize: "0.8rem", backgroundColor: "#f8f9fa" }}>
              <span className="mr-3">
                <i className="fas fa-clock mr-1"></i>
                Dernier poll : <strong>{lastPollTime ? formatTime(lastPollTime.getTime()) : "-"}</strong>
              </span>
              <span className="mr-3">
                <i className="fas fa-camera mr-1"></i>
                Détections : <strong>{detectionCount}</strong>
              </span>
              <span className="mr-3">
                <i className="fas fa-user mr-1"></i>
                Actifs : <strong>{activeFaceCount}</strong>
              </span>
              <span className="mr-3">
                <i className="fas fa-brain mr-1"></i>
                Décisions : <strong>{decisionCount}</strong>
              </span>
              {paused && (
                <span className="badge badge-warning ml-auto">
                  <i className="fas fa-pause mr-1"></i>En pause
                </span>
              )}
              {pollError && !paused && (
                <span className="badge badge-danger ml-auto" title={pollError}>
                  <i className="fas fa-exclamation-triangle mr-1"></i>Erreur de poll
                </span>
              )}
            </div>

            <div className="modal-body p-0" style={{ minHeight: "400px" }}>
              {loading && !snapshot && (
                <div className="text-center py-5">
                  <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2 text-muted">Interrogation du module IA en cours...</p>
                </div>
              )}

              {!loading && !snapshot && !pollError && (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-ghost fa-3x mb-3 d-block"></i>
                  Aucune donnée disponible.
                </div>
              )}

              {!loading && !snapshot && pollError && (
                <div className="alert alert-danger m-3">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  {pollError}
                </div>
              )}

              {snapshot && (
                <div className="row no-gutters" style={{ minHeight: "400px" }}>
                  {/* Active Faces — left ~25% */}
                  <div className="col-3 border-right p-2" style={{ overflowY: "auto", maxHeight: "65vh" }}>
                    <h6 className="text-muted text-uppercase text-center mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      <i className="fas fa-user-friends mr-1"></i> Visages actifs
                    </h6>
                    <ActiveFacesPanel activeFaces={snapshot.activeFaces} />
                  </div>

                  {/* Raw Detections — center ~40% */}
                  <div className="col-5 border-right p-2">
                    <h6 className="text-muted text-uppercase text-center mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      <i className="fas fa-eye mr-1"></i> Détections brutes
                    </h6>
                    <DetectionsPanel detections={snapshot.detections} />
                  </div>

                  {/* Decisions — right ~35% */}
                  <div className="col-4 p-2">
                    <h6 className="text-muted text-uppercase text-center mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                      <i className="fas fa-brain mr-1"></i> Décisions IA
                    </h6>
                    <DecisionsPanel decisions={snapshot.decisions} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer py-2">
              <button type="button" className="btn btn-default" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}
